import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ngn = n => `\u20A6${Math.round(n).toLocaleString()}`;

const AMOUNT_PRESETS = {
  food_delivery:   [{ label: 'Lunch (₦3,500)', amount: 3500 }, { label: 'Family meal (₦8,500)', amount: 8500 }],
  ecommerce:       [{ label: 'Quick order (₦12,000)', amount: 12000 }, { label: 'Bigger basket (₦35,000)', amount: 35000 }],
  groceries:       [{ label: 'Weekly run (₦22,000)', amount: 22000 }],
  utility_airtime: [{ label: '₦1,000 airtime', amount: 1000 }, { label: '₦5,000 data', amount: 5000 }],
  self_onboarded:  [{ label: 'Custom (₦7,500)', amount: 7500 }, { label: 'Custom (₦18,000)', amount: 18000 }],
};

export default function APIPartnerStrip({ csId, onPurchased }) {
  const [vendors, setVendors] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/demo/gdl/vendors`).then(r => setVendors(r.data));
  }, []);

  const buy = async (v, amount, label) => {
    if (!csId) return;
    setBusyId(v.id);
    try {
      const { data } = await axios.post(
        `${API}/api/demo/gdl/vendors/purchase/${csId}`,
        { vendor_id: v.id, amount, item_description: label },
        { withCredentials: true },
      );
      setSuccessMsg({
        vendor: v.name,
        reference: data.reference,
        atom_note: data.atom_note?.text,
        particle: data.atom_note?.particle,
      });
      onPurchased && onPurchased();
      setTimeout(() => setSuccessMsg(null), 4500);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Purchase failed');
    } finally { setBusyId(null); }
  };

  const apiPartners = vendors.filter(v => v.partner_type === 'api_partner');
  const selfOnboarded = vendors.filter(v => v.partner_type === 'self_onboarded');

  return (
    <div data-testid="api-partner-strip" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#2D0A10' }}>
          API Partners
        </div>
        <div style={{ fontSize: 10, color: '#A07078', fontWeight: 600 }}>
          Integrated checkout
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {apiPartners.map(v => (
          <VendorCard key={v.id} v={v} onBuy={buy} busy={busyId === v.id} />
        ))}
      </div>

      {selfOnboarded.length > 0 && (
        <>
          <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#2D0A10' }}>
              Self-onboarded merchants
            </div>
            <div style={{ fontSize: 10, color: '#A07078', fontWeight: 600 }}>
              Direct sellers
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            {selfOnboarded.map(v => (
              <VendorCard key={v.id} v={v} onBuy={buy} busy={busyId === v.id} wide />
            ))}
          </div>
        </>
      )}

      {successMsg && (
        <div data-testid="vendor-purchase-success" style={{
          padding: '12px 14px',
          background: 'linear-gradient(135deg, #ECFDF5, #fff)',
          border: '1px solid #10B981', borderRadius: 14,
          animation: 'gdlFadeIn 0.3s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#059669', fontWeight: 700, fontSize: 13 }}>
            <Check size={16} /> Paid {successMsg.vendor} · {successMsg.reference}
          </div>
          {successMsg.atom_note && (
            <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8,
              background: 'rgba(176,49,64,0.06)', fontSize: 11, color: '#5A2A33',
              display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={12} color="#B03140" />
              <span><b style={{ color: '#B03140' }}>{successMsg.particle}:</b> {successMsg.atom_note}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VendorCard({ v, onBuy, busy, wide }) {
  const [open, setOpen] = useState(false);
  const presets = AMOUNT_PRESETS[v.category] || [{ label: 'Pay ₦5,000', amount: 5000 }];
  return (
    <div data-testid={`vendor-card-${v.slug}`} style={{
      background: '#fff', borderRadius: 14, padding: 12,
      border: '1px solid rgba(176,49,64,0.06)',
      boxShadow: '0 2px 8px rgba(176,49,64,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {v.logo_url ? (
          <img src={v.logo_url} alt={v.name} style={{
            width: 36, height: 36, borderRadius: 8, objectFit: 'contain',
            background: '#fff', border: '1px solid #eee',
          }} />
        ) : (
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: v.accent_color, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800,
          }}>{v.name.slice(0, 2).toUpperCase()}</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#2D0A10' }}>{v.name}</div>
          <div style={{ fontSize: 10, color: '#A07078', overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {v.tagline}
          </div>
        </div>
      </div>

      {!open && (
        <button onClick={() => setOpen(true)} data-testid={`vendor-open-${v.slug}`}
          style={{
            width: '100%', marginTop: 10, padding: '8px',
            background: 'rgba(176,49,64,0.06)', color: '#B03140',
            border: 'none', borderRadius: 8,
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>
          Buy
        </button>
      )}

      {open && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {presets.map(p => (
            <button key={p.label} onClick={() => onBuy(v, p.amount, p.label)} disabled={busy}
              data-testid={`vendor-buy-${v.slug}-${p.amount}`}
              style={{
                padding: '8px 10px',
                background: busy ? '#E5E7EB' : v.accent_color,
                color: busy ? '#9CA3AF' : '#fff',
                border: 'none', borderRadius: 8,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
              {busy ? 'Processing…' : p.label}
            </button>
          ))}
          <button onClick={() => setOpen(false)}
            style={{ padding: '4px', fontSize: 10, color: '#A07078',
              background: 'transparent', border: 'none', cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
